import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { TableContext } from '../context';
import axios from 'axios';
import './SubscriptionManagement.css';
import NotificationSettings from './NotificationSettings';

// Import modular components
import {
    ShareModal,
    AddToListModal,
    ApplicationSettingsRow,
    SubscriptionListForm,
    SubscriptionListCard,
} from './SubscriptionManagement/index';

// Import service functions
import {
    getUserLists,
    createSubscriptionList,
    deleteSubscriptionList,
    cloneSubscriptionList,
    addAppsToList,
    removeAppsFromList,
} from './SubscriptionManagement/apihelpers';

/**
 * SubscriptionManagement Page Component
 * Main page for managing subscriptions and subscription lists
 */
const SubscriptionManagement = () => {
    const {
        graphData,
        subscriptions,
        subscriptionsLoading,
        getSubscriptions,
        tokenFetched,
    } = useContext(TableContext);

    const location = useLocation();

    // Tab state
    const [activeTab, setActiveTab] = useState('subscriptions');

    // Subscribed applications state
    const [subscribedApps, setSubscribedApps] = useState([]);
    const [expandedApps, setExpandedApps] = useState({});
    const [appChanges, setAppChanges] = useState({});
    const [appSettings, setAppSettings] = useState({});
    const [settingsLoadingMap, setSettingsLoadingMap] = useState({});

    // Subscription lists state
    const [subscriptionLists, setSubscriptionLists] = useState([]);
    const [listsLoading, setListsLoading] = useState(true);
    const [expandedLists, setExpandedLists] = useState({});
    const [isCreatingList, setIsCreatingList] = useState(false);

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');

    // Modal states
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareListData, setShareListData] = useState(null);
    const [addToListModalOpen, setAddToListModalOpen] = useState(false);
    const [addToListApp, setAddToListApp] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [listToDelete, setListToDelete] = useState(null);
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [listToClone, setListToClone] = useState(null);
    const [cloneNewName, setCloneNewName] = useState('');

    // Load data on mount
    useEffect(() => {
        if (tokenFetched) {
            getSubscriptions();
            fetchSubscriptionLists();
        }
    }, [tokenFetched]);

    // Check URL params for tab
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === 'lists') {
            setActiveTab('lists');
        }
    }, [location.search]);

    // Transform subscriptions array to application objects
    useEffect(() => {
        if (subscriptions && subscriptions.length > 0) {
            const apps = subscriptions.map((appName, index) => ({
                id: `app-${index}`,
                name: appName.trim(),
                appId: null,
                notificationSettings: appSettings[appName.trim()] || {
                    upstreamSubscribed: false,
                    downstreamSubscribed: false,
                    notificationType: 'false',
                    subscribedStates: [],
                },
            }));
            setSubscribedApps(apps);
        } else {
            setSubscribedApps([]);
        }
    }, [subscriptions, appSettings]);

    /**
     * Fetch subscription lists from API
     */
    const fetchSubscriptionLists = async () => {
        if (!graphData?.mail) return;

        setListsLoading(true);
        try {
            const response = await getUserLists(graphData.mail);
            if (response.status === 200) {
                setSubscriptionLists(response.data.lists || []);
            }
        } catch (error) {
            console.error('Failed to fetch subscription lists:', error);
            // Don't show error toast if user has no lists
            if (!error.message?.includes('User not found')) {
                toast.error('Failed to load subscription lists', {
                    position: 'top-center',
                });
            }
            setSubscriptionLists([]);
        } finally {
            setListsLoading(false);
        }
    };

    /**
     * Fetch notification settings for an application
     */
    const fetchAppSettings = async (appName) => {
        setSettingsLoadingMap((prev) => ({ ...prev, [appName]: true }));

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/getSubscriptionSettings`,
                {
                    user_email: graphData?.mail,
                    app_name: appName,
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('jwt_token'),
                    },
                }
            );

            if (response.data.status === 200) {
                const settings = response.data.settings;
                setAppSettings((prev) => ({
                    ...prev,
                    [appName]: {
                        applicationID: settings.application_id,
                        upstreamSubscribed: settings.upstream_subscribed,
                        downstreamSubscribed: settings.downstream_subscribed,
                        notificationType: settings.notification_type || 'false',
                        subscribedStates: settings.subscribed_states || [],
                    },
                }));
            }
        } catch (error) {
            console.error('Failed to fetch app settings:', error);
        } finally {
            setSettingsLoadingMap((prev) => ({ ...prev, [appName]: false }));
        }
    };

    /**
     * Toggle application expansion
     */
    const handleToggleAppExpand = (appId) => {
        const app = subscribedApps.find((a) => a.id === appId);
        if (app && !expandedApps[appId] && !appSettings[app.name]) {
            fetchAppSettings(app.name);
        }

        setExpandedApps((prev) => ({
            ...prev,
            [appId]: !prev[appId],
        }));
    };

    /**
     * Update application settings locally
     */
    const handleUpdateAppSettings = (appId, settings) => {
        const app = subscribedApps.find((a) => a.id === appId);
        if (!app) return;

        setAppSettings((prev) => ({
            ...prev,
            [app.name]: {
                ...prev[app.name],
                ...settings,
            },
        }));

        setAppChanges((prev) => ({
            ...prev,
            [appId]: true,
        }));
    };

    /**
     * Save application settings to API
     */
    const handleSaveAppSettings = async (app) => {
        const settings = appSettings[app.name];
        if (!settings) return;

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/updateSubscription`,
                {
                    user_email: graphData?.mail,
                    app_name: app.name,
                    upstream_subscribed: settings.upstreamSubscribed,
                    downstream_subscribed: settings.downstreamSubscribed,
                    notification_type: settings.notificationType,
                    subscribed_states: settings.subscribedStates,
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('jwt_token'),
                    },
                }
            );

            if (response.data.status === 200) {
                toast.success(`Settings updated for ${app.name}`, {
                    position: 'top-center',
                    autoClose: 3000,
                });
                setAppChanges((prev) => ({
                    ...prev,
                    [app.id]: false,
                }));
            }
        } catch (error) {
            toast.error('Failed to save settings', {
                position: 'top-center',
                autoClose: 3000,
            });
        }
    };

    /**
     * Unsubscribe from an application
     */
    const handleUnsubscribe = async (app) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/unsubscribe`,
                {
                    user_email: graphData?.mail,
                    app_name: [app.name],
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('jwt_token'),
                    },
                }
            );

            if (response.data.status === 200) {
                toast.success(`Unsubscribed from ${app.name}`, {
                    position: 'top-center',
                    autoClose: 3000,
                });
                getSubscriptions();
            }
        } catch (error) {
            toast.error('Failed to unsubscribe', {
                position: 'top-center',
                autoClose: 3000,
            });
        }
    };

    /**
     * Open Add to List modal
     */
    const handleOpenAddToList = (app) => {
        setAddToListApp(app);
        setAddToListModalOpen(true);
    };

    /**
     * Add application to lists
     */
    const handleAddToLists = async (listIds) => {
        if (!addToListApp) return;

        try {
            for (const listId of listIds) {
                await addAppsToList({
                    userName: graphData?.displayName,
                    userEmail: graphData?.mail,
                    listId,
                    appNames: [addToListApp.name],
                });
            }
            fetchSubscriptionLists();
        } catch (error) {
            throw error;
        }
    };

    /**
     * Remove application from lists
     */
    const handleRemoveFromLists = async (listIds) => {
        if (!addToListApp) return;

        try {
            for (const listId of listIds) {
                await removeAppsFromList({
                    userName: graphData?.displayName,
                    userEmail: graphData?.mail,
                    listId,
                    appNames: [addToListApp.name],
                });
            }
            fetchSubscriptionLists();
        } catch (error) {
            throw error;
        }
    };

    // ========================
    // Subscription List Handlers
    // ========================

    /**
     * Toggle list expansion
     */
    const handleToggleListExpand = (listId) => {
        setExpandedLists((prev) => ({
            ...prev,
            [listId]: !prev[listId],
        }));
    };

    /**
     * Create new subscription list
     */
    const handleCreateList = async ({ listName, description, appNames }) => {
        await createSubscriptionList({
            userName: graphData?.displayName,
            userEmail: graphData?.mail,
            listName,
            appNames,
            description,
        });
        fetchSubscriptionLists();
    };

    /**
     * Share list
     */
    const handleShareList = (list) => {
        setShareListData(list);
        setShareModalOpen(true);
    };

    /**
     * Clone list
     */
    const handleCloneList = (list) => {
        setListToClone(list);
        setCloneNewName(`${list.list_name} (Copy)`);
        setCloneModalOpen(true);
    };

    const handleConfirmClone = async () => {
        if (!listToClone || !cloneNewName.trim()) return;

        try {
            await cloneSubscriptionList({
                userName: graphData?.displayName,
                userEmail: graphData?.mail,
                listId: listToClone.list_id,
                newListName: cloneNewName.trim(),
            });
            toast.success('List cloned successfully', {
                position: 'top-center',
            });
            fetchSubscriptionLists();
            setCloneModalOpen(false);
            setListToClone(null);
            setCloneNewName('');
        } catch (error) {
            toast.error(error.message || 'Failed to clone list', {
                position: 'top-center',
            });
        }
    };

    /**
     * Delete list
     */
    const handleDeleteList = (list) => {
        setListToDelete(list);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!listToDelete) return;

        try {
            await deleteSubscriptionList(graphData?.mail, listToDelete.list_id);
            toast.success('List deleted successfully', {
                position: 'top-center',
            });
            fetchSubscriptionLists();
            setDeleteConfirmOpen(false);
            setListToDelete(null);
        } catch (error) {
            toast.error(error.message || 'Failed to delete list', {
                position: 'top-center',
            });
        }
    };

    /**
     * Add apps to list
     */
    const handleAddAppsToList = async (listId, appNames) => {
        await addAppsToList({
            userName: graphData?.displayName,
            userEmail: graphData?.mail,
            listId,
            appNames,
        });
        await fetchSubscriptionLists();
    };

    /**
     * Remove app from list
     */
    const handleRemoveAppFromList = async (listId, appName) => {
        try {
            await removeAppsFromList({
                userName: graphData?.displayName,
                userEmail: graphData?.mail,
                listId,
                appNames: [appName],
            });
            toast.success('Application removed from list', {
                position: 'top-center',
            });
            await fetchSubscriptionLists();
        } catch (error) {
            toast.error(error.message || 'Failed to remove application', {
                position: 'top-center',
            });
        }
    };

    /**
     * Update app settings within a list
     */
    const handleUpdateListAppSettings = (listId, appId, settings) => {
        // Find the list and app
        const list = subscriptionLists.find((l) => l.list_id === listId);
        if (!list) return;

        const app = list.applications?.find((a) => (a.app_id || a.id) === appId);
        if (!app) return;

        // Update the list's applications with new settings
        setSubscriptionLists((prev) =>
            prev.map((l) =>
                l.list_id === listId
                    ? {
                          ...l,
                          applications: l.applications?.map((a) =>
                              (a.app_id || a.id) === appId
                                  ? {
                                        ...a,
                                        upstream_subscribed: settings.upstreamSubscribed ?? a.upstream_subscribed,
                                        downstream_subscribed: settings.downstreamSubscribed ?? a.downstream_subscribed,
                                        notification_type: settings.notificationType ?? a.notification_type,
                                        subscribed_states: settings.subscribedStates ?? a.subscribed_states,
                                    }
                                  : a
                          ),
                      }
                    : l
            )
        );
    };

    /**
     * Save app settings within a list
     */
    const handleSaveListAppSettings = async (listId, app) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/updateSubscription`,
                {
                    user_email: graphData?.mail,
                    app_name: app.name,
                    upstream_subscribed: app.notificationSettings?.upstreamSubscribed ?? false,
                    downstream_subscribed: app.notificationSettings?.downstreamSubscribed ?? false,
                    notification_type: app.notificationSettings?.notificationType || 'false',
                    subscribed_states: app.notificationSettings?.subscribedStates || [],
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('jwt_token'),
                    },
                }
            );

            if (response.data.status === 200) {
                toast.success(`Settings updated for ${app.name}`, {
                    position: 'top-center',
                    autoClose: 3000,
                });
                await fetchSubscriptionLists();
            }
        } catch (error) {
            console.error('Error saving list app settings:', error);
            toast.error('Failed to save settings', {
                position: 'top-center',
                autoClose: 3000,
            });
        }
    };

    // Filter subscribed apps
    const filteredApps = subscribedApps.filter((app) =>
        app.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Filter subscription lists
    const filteredLists = subscriptionLists.filter((list) =>
        list.list_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Prepare lists for AddToListModal
    const listsForModal = subscriptionLists.map((list) => ({
        id: list.list_id,
        name: list.list_name,
        appCount: list.app_count || 0,
        hasApplication: list.applications?.some(
            (app) => app.app_name?.toLowerCase() === addToListApp?.name?.toLowerCase()
        ) || false,
    }));

    return (
        <div className="subscription-management-container">
            {/* Header */}
            <div className="subscription-header">
                <h1>Subscription Settings</h1>
                {activeTab === 'lists' && (
                    <div className="header-stats">
                        {subscriptionLists.length} Subscription List{subscriptionLists.length !== 1 ? 's' : ''}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="subscription-tabs">
                <button
                    className={`tab-btn ${activeTab === 'subscriptions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subscriptions')}
                >
                    Subscribed Applications
                </button>
                <button
                    className={`tab-btn ${activeTab === 'lists' ? 'active' : ''}`}
                    onClick={() => setActiveTab('lists')}
                >
                    Subscription Lists
                </button>
            </div>

            {/* Search/Filter - Only show for Lists tab */}
            {activeTab === 'lists' && (
                <div className="filter-bar">
                    <div className="filter-left">
                        <FilterListIcon className="filter-icon" />
                        <input
                            type="text"
                            placeholder="Search lists..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="filter-input"
                        />
                        {searchQuery && (
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </div>

                    {!isCreatingList && (
                        <button
                            className="animated-search-button create-list-btn"
                            onClick={() => setIsCreatingList(true)}
                        >
                            <AddIcon /> Create Subscription List
                        </button>
                    )}
                </div>
            )}

            {/* Content */}
            <div className="subscription-content">
                {/* Subscribed Applications Tab */}
                {activeTab === 'subscriptions' && (
                    <NotificationSettings />
                )}

                {/* Subscription Lists Tab */}
                {activeTab === 'lists' && (
                    <div className="lists-container">
                        {/* Create List Form */}
                        {isCreatingList && (
                            <SubscriptionListForm
                                onCancel={() => setIsCreatingList(false)}
                                onCreate={handleCreateList}
                                existingListsCount={subscriptionLists.length}
                                userName={graphData?.displayName}
                            />
                        )}

                        {/* Lists */}
                        {listsLoading ? (
                            <div className="loading-container">
                                <CircularProgress />
                                <p>Loading subscription lists...</p>
                            </div>
                        ) : filteredLists.length > 0 ? (
                            <div className="subscription-lists-container">
                                {filteredLists.map((list) => (
                                    <SubscriptionListCard
                                        key={list.list_id}
                                        list={list}
                                        isExpanded={expandedLists[list.list_id]}
                                        onToggleExpand={handleToggleListExpand}
                                        onShare={handleShareList}
                                        onClone={handleCloneList}
                                        onDelete={handleDeleteList}
                                        onAddApps={handleAddAppsToList}
                                        onRemoveApp={handleRemoveAppFromList}
                                        onUpdateAppSettings={handleUpdateListAppSettings}
                                        onSaveAppSettings={handleSaveListAppSettings}
                                        permission={list.permission || 'read'}
                                        graphData={graphData}
                                    />
                                ))}
                            </div>
                        ) : !isCreatingList ? (
                            <div className="empty-state lists-empty">
                                {searchQuery ? (
                                    <>
                                        <p>No lists match "{searchQuery}"</p>
                                        <button
                                            className="animated-search-button"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            Clear Search
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="empty-icon-large">📋</div>
                                        <p>No subscription lists yet.</p>
                                        <p className="empty-hint">
                                            Create your first list to group applications together.
                                        </p>
                                        <button
                                            className="animated-search-button"
                                            onClick={() => setIsCreatingList(true)}
                                        >
                                            <AddIcon /> Create Subscription List
                                        </button>
                                    </>
                                )}
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            {/* Share Modal */}
            <ShareModal
                open={shareModalOpen}
                onClose={() => {
                    setShareModalOpen(false);
                    setShareListData(null);
                }}
                listId={shareListData?.list_id}
                listName={shareListData?.list_name}
                userEmail={graphData?.mail}
                userName={graphData?.displayName}
                onShareSuccess={fetchSubscriptionLists}
            />

            {/* Add to List Modal */}
            <AddToListModal
                open={addToListModalOpen}
                onClose={() => {
                    setAddToListModalOpen(false);
                    setAddToListApp(null);
                }}
                applicationName={addToListApp?.name}
                applicationId={addToListApp?.appId}
                existingLists={listsForModal}
                onAddToLists={handleAddToLists}
                onRemoveFromLists={handleRemoveFromLists}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Subscription List</DialogTitle>
                <DialogContent>
                    <p>
                        Are you sure you want to delete "{listToDelete?.list_name}"? This action
                        cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions>
                    <button
                        className="animated-search-button cancel-btn"
                        onClick={() => setDeleteConfirmOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="animated-search-button delete-btn"
                        onClick={handleConfirmDelete}
                    >
                        Delete
                    </button>
                </DialogActions>
            </Dialog>

            {/* Clone Dialog */}
            <Dialog
                open={cloneModalOpen}
                onClose={() => setCloneModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Clone Subscription List</DialogTitle>
                <DialogContent>
                    <p style={{ marginBottom: '16px' }}>
                        Create a copy of "{listToClone?.list_name}"
                    </p>
                    <TextField
                        fullWidth
                        label="New List Name"
                        value={cloneNewName}
                        onChange={(e) => setCloneNewName(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <button
                        className="animated-search-button cancel-btn"
                        onClick={() => setCloneModalOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="animated-search-button"
                        onClick={handleConfirmClone}
                        disabled={!cloneNewName.trim()}
                    >
                        Clone List
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default SubscriptionManagement;
